import theme from "../../themes";
import { formatSecondsToTime } from "../../utils/formatDecimalNumber";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { getCallStatusLabel } from "../../constants/callStatus";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";

dayjs.extend(utc);

interface CallTimeSort {
  order: "ascend" | "descend";
  onToggle: () => void;
}

export const generateDynamicColumns = (
  sampleData: any[],
  t: any,
  onShowSummaryDetail?: (record: any) => void,
  callTimeSort?: CallTimeSort
) => {
  const baseColumns = [
    {
      title: t("reports.tableColumns.recipientName"),
      dataIndex: "recipient_name",
      key: "recipient_name",
      render: (text: string) => (
        <span
          style={{
            color: theme.colors.blue1,
            fontWeight: 500,
            cursor: "default",
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: t("reports.tableColumns.phoneNumber"),
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: t("reports.tableColumns.duration"),
      dataIndex: "duration",
      key: "duration",
      render: (duration: number) => formatSecondsToTime(duration),
    },
    {
      title: t("reports.tableColumns.status") || t("status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => getCallStatusLabel(status),
    },
    {
      title: callTimeSort ? (
        <span
          style={{ cursor: "pointer", color: "#000" }}
          onClick={callTimeSort.onToggle}
        >
          {t("reports.tableColumns.callTime")}{" "}
          {callTimeSort.order === "ascend" ? (
            <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
          ) : (
            <ArrowDownIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
          )}
        </span>
      ) : (
        t("reports.tableColumns.callTime")
      ),
      dataIndex: "start_time",
      key: "call_time",
      render: (callTime: string) =>
        callTime ? dayjs(callTime).format("hh:mm A, MMM DD, YYYY") : "-",
    },
  ];

  const summaryColumn = {
    title: t("reports.tableColumns.summary"),
    dataIndex: "call_analysis",
    key: "summary",
    width: 300,
    render: (callAnalysis: any, record: any) => {
      const summary = callAnalysis?.summary;
      const followUp = callAnalysis?.follow_up;
      const reason = callAnalysis?.reason;
      const followUpRecommendation =
        typeof followUp === "string" ? followUp : followUp?.recommendation;

      const hasInline = Boolean(summary);
      const hasDetail = Boolean(reason || followUpRecommendation);

      const handleShowDetail = () => {
        if (!hasDetail && !hasInline) return;
        if (onShowSummaryDetail) {
          onShowSummaryDetail(record);
        }
      };

      if (!hasInline && !hasDetail) {
        return record?.summary || "-";
      }

      return (
        <div style={{ lineHeight: "1.5" }}>
          {summary && (
            <div>
              <span style={{ fontWeight: 600 }}>Summary:</span> {summary}
            </div>
          )}
          {hasDetail && (
            <div style={{ marginTop: 4 }}>
              <span
                onClick={handleShowDetail}
                style={{
                  color: theme.colors.blue1,
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {t("common.detail") || "Detail"}
              </span>
            </div>
          )}
        </div>
      );
    },
  };

  if (!sampleData || sampleData.length === 0)
    return [...baseColumns, summaryColumn];

  // Collect union of all action keys and the first available description per key
  const actionKeySet = new Set<string>();
  const actionKeyToDescription: Record<string, string | undefined> = {};

  for (const record of sampleData) {
    const action = record?.action || {};
    Object.entries(action).forEach(([key, value]: [string, any]) => {
      const isActionTaken =
        value && typeof value === "object" && value.action_taken === true;

      if (isActionTaken) {
        actionKeySet.add(key);
        if (actionKeyToDescription[key] === undefined && value.description) {
          actionKeyToDescription[key] = value.description;
        }
      }
    });
  }

  if (actionKeySet.size === 0) return [...baseColumns, summaryColumn];

  if (actionKeySet.has("end_call")) {
    actionKeySet.delete("end_call");
  }

  const actionColumns = Array.from(actionKeySet).map((key) => {
    return {
      title: (
        <span>
          {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
          <Tooltip
            title={
              actionKeyToDescription[key] ||
              `Information about ${key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}`
            }
          >
            <InfoCircleOutlined
              style={{ color: theme.colors.grey1, fontSize: 14 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: ["action", key],
      key: key,
      render: (actionData: any) => {
        if (
          !actionData?.value ||
          (Array.isArray(actionData.value) && actionData.value.length === 0)
        ) {
          return "-";
        }

        const value = actionData.value;
        const items = Array.isArray(value) ? value : [value];

        const formatTimeSlot = (slot: any) => {
          const { start_time, end_time } = slot || {};
          if (!start_time && !end_time) return "-";
          try {
            const startTime = start_time ? dayjs.utc(start_time) : null;
            const endTime = end_time ? dayjs.utc(end_time) : null;
            if (!startTime && endTime)
              return `${endTime.format("h:mm A")}, ${endTime.format(
                "MMM DD, YYYY"
              )}`;
            if (startTime && !endTime)
              return `${startTime.format("h:mm A")}, ${startTime.format(
                "MMM DD, YYYY"
              )}`;
            if (startTime?.isValid() && endTime?.isValid()) {
              if (startTime.isSame(endTime, "day")) {
                return `${startTime.format("h:mm A")} - ${endTime.format(
                  "h:mm A"
                )}, ${startTime.format("MMM DD, YYYY")}`;
              } else {
                return `${startTime.format("h:mm A")}, ${startTime.format(
                  "MMM DD, YYYY"
                )} - ${endTime.format("h:mm A")}, ${endTime.format(
                  "MMM DD, YYYY"
                )}`;
              }
            }
            return (
              `${start_time || ""} - ${end_time || ""}`.replace(
                /^- | -$/,
                ""
              ) || "-"
            );
          } catch (error) {
            return (
              `${start_time || ""} - ${end_time || ""}`.replace(
                /^- | -$/,
                ""
              ) || "-"
            );
          }
        };

        const toLabel = (key: string) =>
          key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

        const normalizeValue = (val: any) => {
          if (typeof val === "string") {
            // If contains HTML tags, strip tags and show inner text
            if (/[<>]/.test(val)) {
              return val.replace(/<[^>]*>/g, "").trim() || val;
            }
            return val;
          }
          return JSON.stringify(val);
        };

        const renderItem = (item: any) => {
          if (item && typeof item === "object") {
            const keys = Object.keys(item);
            const hasTime = Boolean(item.start_time || item.end_time);
            const otherKeys = hasTime
              ? keys.filter((k) => k !== "start_time" && k !== "end_time")
              : keys;
            return (
              <div>
                {hasTime && (
                  <div>
                    {(() => {
                      const hasStart = Boolean(item.start_time);
                      const hasEnd = Boolean(item.end_time);
                      const label =
                        hasStart && hasEnd
                          ? `${toLabel("start_time")} - ${toLabel("end_time")}:`
                          : hasStart
                          ? `${toLabel("start_time")}:`
                          : `${toLabel("end_time")}:`;
                      return (
                        <>
                          <span style={{ fontWeight: 600 }}>{label}</span>{" "}
                          {formatTimeSlot(item)}
                        </>
                      );
                    })()}
                  </div>
                )}
                {otherKeys.map((k) => (
                  <div key={k} style={{ marginTop: "4px" }}>
                    <span style={{ fontWeight: 600 }}>{`${toLabel(k)}:`}</span>{" "}
                    {normalizeValue(item[k])}
                  </div>
                ))}
              </div>
            );
          }
          return <div>{normalizeValue(item)}</div>;
        };

        return (
          <div>
            {items.map((it: any, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: index < items.length - 1 ? "8px" : "0",
                  lineHeight: "1.4",
                }}
              >
                {renderItem(it)}
              </div>
            ))}
          </div>
        );
      },
    };
  });

  return [...baseColumns, ...actionColumns, summaryColumn];
};
